import { Outlet, Link, useNavigate } from 'react-router-dom'
import { Scale, LogOut, HelpCircle, Settings, ChevronDown, MessageSquare, LayoutDashboard, Menu, X } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useOnboardingStore } from '../stores/onboardingStore'
import UsageWarningBanner from './UsageWarningBanner'
import BottomNavigation from './BottomNavigation'
import LanguageSelector from './LanguageSelector'
import Footer from './Footer'
import { useUsage } from '../hooks/useUsage'
import { trackTourRestarted } from '../utils/analytics'
import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useRTL } from '../hooks/useRTL'

// Force Netlify rebuild - deployed 2025-11-06
export default function Layout() {
  const { isRTL } = useRTL()

  // Set direction attribute on document.documentElement
  document.documentElement.dir = isRTL ? 'rtl' : 'ltr'
  const { isAuthenticated, user, logout } = useAuth()
  const navigate = useNavigate()
  // Only fetch usage data when user is authenticated (prevents 401 errors on homepage)
  const { usage, limit, percentage, isCritical } = useUsage(isAuthenticated, 60000)
  const { resetTour } = useOnboardingStore()
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const accountMenuRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslation()

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const handleRestartTour = () => {
    trackTourRestarted('navbar')
    resetTour()
    setIsAccountMenuOpen(false)
    // Navigate to chat page if not already there
    if (window.location.pathname !== '/chat') {
      navigate('/chat')
    }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target as Node)) {
        setIsAccountMenuOpen(false)
      }
    }

    if (isAccountMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isAccountMenuOpen])

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isMobileMenuOpen])

  return (
    <div className="min-h-screen flex flex-col">
      {/* Skip to main content link - WCAG 2.1 AA */}
      <a href="#main-content" className="skip-link sr-only focus:not-sr-only">
        {t('common.skipToContent')}
      </a>

      {/* Usage Warning Banner (95% usage) - shown on all pages when authenticated */}
      {isAuthenticated && isCritical && (
        <UsageWarningBanner usage={usage} limit={limit} percentage={percentage} />
      )}

      <header className="border-b flex-shrink-0">
        <nav className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Scale className="w-6 h-6 text-blue-600" />
              <div className="flex flex-col">
                <span className="text-xl font-bold">CodiceCivile.AI</span>
                <span className="text-[10px] text-gray-500 -mt-1 hidden md:block">Expert Legal Tech AI Team</span>
              </div>
            </Link>

            <div className="flex items-center gap-4">
              {/* Mobile Menu Button (Hamburger) - visible only on mobile */}
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="md:hidden p-3 min-h-[48px] min-w-[48px] flex items-center justify-center hover:bg-gray-100 rounded-md transition-colors"
                aria-label="Open mobile menu"
              >
                <Menu className="w-6 h-6 text-gray-700" />
              </button>

              {/* Language Selector */}
              <LanguageSelector />

              {/* Desktop Navigation Links - hidden on mobile, fixed touch targets */}
              <Link to="/solutions" className="text-sm font-medium hover:text-blue-600 px-4 py-3 min-h-[48px] inline-flex items-center hidden md:inline-flex">
                {isAuthenticated ? t('nav.chat') : t('nav.solutions')}
              </Link>

              <Link to="/how-it-works" className="text-sm font-medium hover:text-blue-600 px-4 py-3 min-h-[48px] inline-flex items-center hidden lg:inline-flex">
                {t('nav.how-it-works')}
              </Link>

              <Link to="/massime" className="text-sm font-medium hover:text-blue-600 px-4 py-3 min-h-[48px] inline-flex items-center hidden md:inline-flex">
                {t('nav.massime')}
              </Link>

              <Link to="/referrals" className="text-sm font-medium hover:text-blue-600 px-4 py-3 min-h-[48px] inline-flex items-center hidden lg:inline-flex">
                {t('nav.invite')}
              </Link>

              <Link to="/pricing" className="text-sm font-medium hover:text-blue-600 px-4 py-3 min-h-[48px] inline-flex items-center hidden md:inline-flex">
                {t('nav.pricing')}
              </Link>

              <Link to="/guide" className="text-sm font-medium hover:text-blue-600 px-4 py-3 min-h-[48px] inline-flex items-center hidden lg:inline-flex">
                {t('nav.guide')}
              </Link>

              <Link to="/developers" className="text-sm font-medium hover:text-blue-600 px-4 py-3 min-h-[48px] inline-flex items-center hidden lg:inline-flex">
                {t('nav.developers')}
              </Link>

              {isAuthenticated ? (
                <div className="relative" ref={accountMenuRef}>
                  {/* Account Menu Trigger */}
                  <button
                    onClick={() => setIsAccountMenuOpen(!isAccountMenuOpen)}
                    className="flex items-center gap-2 px-3 py-2 min-h-touch bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  >
                    <div className="w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-medium">
                      {user?.email?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <span className="text-sm text-gray-700 hidden md:inline max-w-[120px] truncate">
                      {user?.email?.split('@')[0]}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-gray-600 transition-transform ${isAccountMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Dropdown Menu */}
                  {isAccountMenuOpen && (
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      {/* User Info Header */}
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900 truncate">{user?.email}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{t('common.account')}</p>
                      </div>

                      {/* Navigation Links */}
                      <div className="py-1">
                        <Link
                          to="/chat"
                          onClick={() => setIsAccountMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <MessageSquare className="w-4 h-4 text-gray-500" />
                          {t('nav.chat')}
                        </Link>
                        <Link
                          to="/dashboard"
                          onClick={() => setIsAccountMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <LayoutDashboard className="w-4 h-4 text-gray-500" />
                          {t('nav.dashboard')}
                        </Link>
                        <Link
                          to="/settings"
                          onClick={() => setIsAccountMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <Settings className="w-4 h-4 text-gray-500" />
                          {t('nav.settings')}
                        </Link>
                        <button
                          onClick={handleRestartTour}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors text-left"
                        >
                          <HelpCircle className="w-4 h-4 text-gray-500" />
                          {t('nav.help')}
                        </button>
                      </div>

                      {/* Logout */}
                      <div className="border-t border-gray-100 pt-1 mt-1">
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
                        >
                          <LogOut className="w-4 h-4" />
                          {t('auth.logout')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Link to="/login" className="text-sm font-medium hover:text-blue-600 px-4 py-3 min-h-[48px] inline-flex items-center hidden md:inline-flex">
                    {t('auth.login')}
                  </Link>
                  <Link
                    to="/register"
                    className="text-sm font-medium bg-blue-600 text-white px-6 py-3 min-h-[48px] rounded-md hover:bg-blue-700 inline-flex items-center hidden md:inline-flex"
                  >
                    {t('auth.getStarted')}
                  </Link>
                </>
              )}
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile Menu Drawer - Full Screen Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          />

          {/* Menu Panel */}
          <nav className="fixed right-0 top-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl transform transition-transform">
            {/* Menu Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-2">
                <Scale className="w-5 h-5 text-blue-600" />
                <span className="font-bold text-lg">{t('common.menu')}</span>
              </div>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-3 min-h-[48px] min-w-[48px] flex items-center justify-center hover:bg-gray-100 rounded-md transition-colors"
                aria-label={t('common.close')}
              >
                <X className="w-6 h-6 text-gray-700" />
              </button>
            </div>

            {/* Menu Links */}
            <div className="flex flex-col p-4 space-y-1">
              <Link
                to="/solutions"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 px-4 py-4 min-h-[48px] rounded-md flex items-center transition-colors"
              >
                {t('nav.solutions')}
              </Link>

              <Link
                to="/how-it-works"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 px-4 py-4 min-h-[48px] rounded-md flex items-center transition-colors"
              >
                {t('nav.how-it-works')}
              </Link>

              <Link
                to="/massime"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 px-4 py-4 min-h-[48px] rounded-md flex items-center transition-colors"
              >
                {t('nav.massime')}
              </Link>

              <Link
                to="/referrals"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 px-4 py-4 min-h-[48px] rounded-md flex items-center transition-colors"
              >
                {t('nav.invite')}
              </Link>

              <Link
                to="/pricing"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 px-4 py-4 min-h-[48px] rounded-md flex items-center transition-colors"
              >
                {t('nav.pricing')}
              </Link>

              <Link
                to="/guide"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 px-4 py-4 min-h-[48px] rounded-md flex items-center transition-colors"
              >
                {t('nav.guide')}
              </Link>

              <Link
                to="/developers"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 px-4 py-4 min-h-[48px] rounded-md flex items-center transition-colors"
              >
                {t('nav.developers')}
              </Link>

              {!isAuthenticated && (
                <>
                  <div className="border-t my-2" />

                  <Link
                    to="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 px-4 py-4 min-h-[48px] rounded-md flex items-center transition-colors"
                  >
                    {t('auth.login')}
                  </Link>

                  <Link
                    to="/register"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-base font-medium bg-blue-600 text-white hover:bg-blue-700 px-4 py-4 min-h-[48px] rounded-md flex items-center justify-center transition-colors"
                  >
                    {t('auth.getStarted')}
                  </Link>
                </>
              )}

              {isAuthenticated && (
                <>
                  <div className="border-t my-2" />

                  <Link
                    to="/chat"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-4 min-h-[48px] text-base text-gray-700 hover:bg-gray-50 hover:text-blue-600 rounded-md transition-colors"
                  >
                    <MessageSquare className="w-5 h-5" />
                    {t('nav.chat')}
                  </Link>

                  <Link
                    to="/dashboard"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-4 min-h-[48px] text-base text-gray-700 hover:bg-gray-50 hover:text-blue-600 rounded-md transition-colors"
                  >
                    <LayoutDashboard className="w-5 h-5" />
                    {t('nav.dashboard')}
                  </Link>

                  <Link
                    to="/settings"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-4 min-h-[48px] text-base text-gray-700 hover:bg-gray-50 hover:text-blue-600 rounded-md transition-colors"
                  >
                    <Settings className="w-5 h-5" />
                    {t('nav.settings')}
                  </Link>

                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false)
                      handleRestartTour()
                    }}
                    className="flex items-center gap-3 px-4 py-4 min-h-[48px] text-base text-gray-700 hover:bg-gray-50 hover:text-blue-600 rounded-md transition-colors text-left w-full"
                  >
                    <HelpCircle className="w-5 h-5" />
                    {t('nav.help')}
                  </button>

                  <div className="border-t my-2" />

                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false)
                      handleLogout()
                    }}
                    className="flex items-center gap-3 px-4 py-4 min-h-[48px] text-base text-red-600 hover:bg-red-50 rounded-md transition-colors text-left w-full"
                  >
                    <LogOut className="w-5 h-5" />
                    {t('auth.logout')}
                  </button>
                </>
              )}
            </div>
          </nav>
        </div>
      )}

      <main id="main-content" className="flex-1">
        <Outlet />
      </main>

      <div className={isAuthenticated ? 'pb-20 md:pb-0' : ''}>
        <Footer />
      </div>

      {/* Mobile Bottom Navigation - Only show for authenticated users */}
      {isAuthenticated && <BottomNavigation />}
    </div>
  )
}
